import ELK, { type ElkExtendedEdge, type ElkNode } from 'elkjs/lib/elk.bundled';
import type { ParsedNode, ParsedEdge, HandleDefinition } from '../types';

const elk = new ELK({
  defaultLayoutOptions: {
    'algorithm': 'layered',
    'elk.direction': 'RIGHT',
    'elk.spacing.nodeNode': '80',
    'elk.layered.spacing.nodeNodeBetweenLayers': '120',
    'elk.layered.spacing.edgeNodeBetweenLayers': '50',
    'elk.layered.spacing.edgeEdgeBetweenLayers': '25',
    'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    'elk.layered.cycleBreaking.strategy': 'GREEDY',
    'elk.edgeRouting': 'ORTHOGONAL',
    'elk.layered.unnecessaryBendpoints': 'false',
    'elk.layered.spacing.baseValue': '50',
    'elk.layered.compaction.postCompaction.strategy': 'NONE',
    'elk.layered.compaction.connectedComponents': 'false'
  }
});

export type NodeLayout = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Calculate node dimensions based on handle count and values.
 * Width is fixed at 230px. Height grows with the number of input/output
 * rows and the number of values entries.
 */
function sizeFor( node: ParsedNode ): { width: number; height: number } {
  const width = 230;

  const baseHeight = 80;
  const rowHeight = 32;
  const rows = Math.max( node.inputs.length, node.outputs.length, 1 );
  const valuesHeight = node.values && Object.keys( node.values ).length > 0
    ? 50 + Object.keys( node.values ).length * 24
    : 0;
  const height = baseHeight + rows * rowHeight + valuesHeight + 20;

  return { width, height };
}

/**
 * Build ELK port descriptors from HandleDefinition arrays.
 */
function buildPorts( nodeId: string, inputs: HandleDefinition[], outputs: HandleDefinition[]) {
  return [
    ...inputs.map(( p, index ) => ({
      id: `${nodeId}:in:${p.name}`,
      properties: { side: 'WEST' },
      layoutOptions: {
        'elk.port.side': 'WEST',
        'elk.port.anchor': '[0,0.5]',
        'elk.port.index': index.toString()
      }
    })),
    ...outputs.map(( p, index ) => ({
      id: `${nodeId}:out:${p.name}`,
      properties: { side: 'EAST' },
      layoutOptions: {
        'elk.port.side': 'EAST',
        'elk.port.anchor': '[1,0.5]',
        'elk.port.index': index.toString()
      }
    }))
  ];
}

/**
 * Run ELK layered layout on parsed nodes and edges.
 *
 * Direction: LEFT-TO-RIGHT, layered algorithm, orthogonal edge routing.
 * Returns a Map from node id to its computed position and dimensions.
 */
export async function layout(
  nodes: ParsedNode[],
  edges: ParsedEdge[]
): Promise<Map<string, NodeLayout>> {
  // Collect edge-referenced handles per node so we can add ports for dynamic handles
  // that aren't declared in the registry (e.g. Input, CodeBlock with custom params)
  const edgeInputsByNode = new Map<string, Set<string>>();
  const edgeOutputsByNode = new Map<string, Set<string>>();
  for ( const e of edges ) {
    if ( e.sourceHandle ) {
      if ( !edgeOutputsByNode.has( e.source )) edgeOutputsByNode.set( e.source, new Set());
      edgeOutputsByNode.get( e.source )!.add( e.sourceHandle );
    }
    if ( e.targetHandle ) {
      if ( !edgeInputsByNode.has( e.target )) edgeInputsByNode.set( e.target, new Set());
      edgeInputsByNode.get( e.target )!.add( e.targetHandle );
    }
  }

  const elkNodes: ElkNode[] = nodes.map(( n ) => {
    // Merge registry handles with edge-inferred handles for port generation
    const inputNames = new Set( n.inputs.map(( h ) => h.name ));
    const extraInputs: HandleDefinition[] = [];
    for ( const name of edgeInputsByNode.get( n.id ) ?? []) {
      if ( !inputNames.has( name )) {
        extraInputs.push({ name, type: 'any' });
      }
    }

    const outputNames = new Set( n.outputs.map(( h ) => h.name ));
    const extraOutputs: HandleDefinition[] = [];
    for ( const name of edgeOutputsByNode.get( n.id ) ?? []) {
      if ( !outputNames.has( name )) {
        extraOutputs.push({ name, type: 'any' });
      }
    }

    const allInputs = [ ...n.inputs, ...extraInputs ];
    const allOutputs = [ ...n.outputs, ...extraOutputs ];

    return {
      id: n.id,
      width: sizeFor( n ).width,
      height: sizeFor( n ).height,
      layoutOptions: {
        'elk.padding': '[top=15,left=15,bottom=15,right=15]',
        'elk.portConstraints': 'FIXED_SIDE',
        'elk.spacing.portPort': '20',
        'elk.port.borderOffset': '5'
      },
      ports: buildPorts( n.id, allInputs, allOutputs )
    };
  });

  const elkEdges: ElkExtendedEdge[] = edges.map(( e ) => ({
    id: e.id,
    sources: [e.sourceHandle ? `${e.source}:out:${e.sourceHandle}` : e.source],
    targets: [e.targetHandle ? `${e.target}:in:${e.targetHandle}` : e.target]
  }));

  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.spacing.nodeNode': '80',
      'elk.layered.spacing.nodeNodeBetweenLayers': '120',
      'elk.layered.spacing.edgeNodeBetweenLayers': '50',
      'elk.layered.spacing.edgeEdgeBetweenLayers': '25',
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.cycleBreaking.strategy': 'GREEDY',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.layered.unnecessaryBendpoints': 'false',
      'elk.layered.spacing.baseValue': '50',
      'elk.padding': '[top=80,left=80,bottom=80,right=80]',
      'elk.portConstraints': 'FIXED_SIDE',
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
      'elk.layered.compaction.postCompaction.strategy': 'NONE',
      'elk.layered.compaction.connectedComponents': 'false',
      'elk.layered.thoroughness': '10'
    },
    children: elkNodes,
    edges: elkEdges
  } as ElkNode;

  const res = await elk.layout( graph );

  const pos = new Map<string, NodeLayout>();
  for ( const c of res.children ?? []) {
    pos.set( c.id!, {
      x: c.x ?? 0,
      y: c.y ?? 0,
      width: c.width ?? 230,
      height: c.height ?? 80
    });
  }
  return pos;
}
